import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

function todayYmd() {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, '0');
  const d = String(n.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function shiftYmd(ymd, delta) {
  const [y, m, d] = String(ymd || '').split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return todayYmd();
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const PAGE_SIZE = 200;

export default function OperateLogsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/count') ? '/count' : '/admin';
  const memberPanelPath = `${basePath}/member-panel`;

  const [date, setDate] = useState(todayYmd());
  const [phoneInput, setPhoneInput] = useState('');
  const [activePhone, setActivePhone] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const phoneMode = Boolean(String(activePhone || '').trim());

  const loadByDate = useCallback(async (ymd) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/operate-logs/today', { params: { date: ymd } });
      const list = Array.isArray(data?.rows) ? data.rows : [];
      setRows(list);
      setTotal(list.length);
      setOffset(0);
    } catch {
      setRows([]);
      setTotal(0);
      setError('讀取失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadByPhone = useCallback(async (phone, nextOffset = 0, append = false) => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/operate-logs/by-phone', {
        params: { phone, limit: PAGE_SIZE, offset: nextOffset },
      });
      const list = Array.isArray(data?.rows) ? data.rows : [];
      setRows((prev) => (append ? [...prev, ...list] : list));
      setTotal(Number(data?.total || 0));
      setOffset(nextOffset + list.length);
    } catch {
      if (!append) setRows([]);
      setTotal(0);
      setError('讀取失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (phoneMode) return undefined;
    loadByDate(date);
    return undefined;
  }, [date, phoneMode, loadByDate]);

  const onSearchPhone = () => {
    const p = String(phoneInput || '').trim();
    if (!p) {
      setActivePhone('');
      return;
    }
    setActivePhone(p);
    loadByPhone(p, 0, false);
  };

  const onClearPhone = () => {
    setPhoneInput('');
    setActivePhone('');
    setOffset(0);
  };

  const subtitle = useMemo(() => {
    if (phoneMode) return `會員電話 ${activePhone} · 共 ${total} 筆`;
    return `日期 ${date} · ${rows.length} 筆`;
  }, [phoneMode, activePhone, total, date, rows.length]);

  const goLookup = (phone) => {
    if (!phone) return;
    navigate(`${memberPanelPath}?phone=${encodeURIComponent(String(phone))}`);
  };

  return (
    <div style={{ padding: '12px 8px 24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>操作明細</h2>
        <span style={{ fontSize: 13, color: '#64748b' }}>{subtitle}</span>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          marginBottom: 14,
          padding: 12,
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: phoneMode ? 0.45 : 1 }}>
          <button type="button" disabled={phoneMode} onClick={() => setDate((d) => shiftYmd(d, -1))}>前一天</button>
          <input
            type="date"
            value={date}
            disabled={phoneMode}
            onChange={(e) => setDate(e.target.value || todayYmd())}
            style={{ padding: '6px 8px', fontSize: 14 }}
          />
          <button type="button" disabled={phoneMode} onClick={() => setDate((d) => shiftYmd(d, 1))}>後一天</button>
          <button type="button" disabled={phoneMode} onClick={() => setDate(todayYmd())}>今天</button>
        </div>

        <div style={{ width: 1, height: 28, background: '#cbd5e1' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: '1 1 220px' }}>
          <input
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onSearchPhone(); }}
            placeholder="會員電話查總歷史"
            style={{ flex: 1, minWidth: 140, padding: '8px 10px', fontSize: 14 }}
          />
          <button type="button" onClick={onSearchPhone} style={{ fontWeight: 700 }}>查詢</button>
          {phoneMode ? (
            <button type="button" onClick={onClearPhone}>清除電話</button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 12, fontSize: 14 }}>讀取中…</div>
      ) : error ? (
        <div style={{ padding: 12, color: '#b91c1c', fontSize: 14 }}>{error}</div>
      ) : (
        <>
          <div style={{ maxHeight: '70vh', overflow: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%', border: '1px solid #eee', borderRadius: 8 }}>
            <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '6px 8px' }} align="left">時間</th>
                  <th style={{ padding: '6px 8px' }} align="left">會員</th>
                  <th style={{ padding: '6px 8px' }} align="left">電話</th>
                  <th style={{ padding: '6px 8px' }} align="left">類型</th>
                  <th style={{ padding: '6px 8px' }} align="right">數量</th>
                  <th style={{ padding: '6px 8px' }} align="left">備註</th>
                  <th style={{ padding: '6px 8px' }} align="left">操作</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.CREATED_TIME}-${r.MEMBER_ID}-${i}`}>
                    <td style={{ padding: '6px 8px' }}>{r.CREATED_TIME ?? ''}</td>
                    <td style={{ padding: '6px 8px' }}>{r.NAME || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.PHONE || '-'}</td>
                    <td style={{ padding: '6px 8px' }}>{r.TYPE || '-'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.AMOUNT ?? ''}</td>
                    <td style={{ padding: '6px 8px' }}>{r.REMARK || ''}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <button
                        type="button"
                        onClick={() => goLookup(r.PHONE)}
                        disabled={!r.PHONE}
                        style={{ padding: '6px 10px', fontSize: 12 }}
                      >
                        查詢
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} style={{ padding: 12, opacity: 0.7 }}>
                      {phoneMode ? '此電話沒有操作紀錄' : '此日期沒有紀錄'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {phoneMode && offset < total ? (
            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <button
                type="button"
                disabled={loading}
                onClick={() => loadByPhone(activePhone, offset, true)}
              >
                載入更多（已顯示 {rows.length}／{total}）
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
